import React from 'react';
import { AppBar, Toolbar, Typography, Box, useTheme, useMediaQuery, Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { usePeerTube } from '../contexts/PeerTubeContext';
import tubeAILogo from '/tubeAI.svg';


const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedInstance, setSelectedInstance, instances } = usePeerTube();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleInstanceChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    const instance = instances.find(inst => inst.url === selectedValue);
    if (instance) {
      setSelectedInstance(instance);
      
      // If we're in the video player view, navigate back to the homepage
      if (location.pathname.includes('/video/')) {
        navigate('/');
      }
    }
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={2}
      sx={{ 
        bgcolor: '#00a278',
        color: 'white',
        width: '100vw', 
        maxWidth: '100%',
        left: 0,
        right: 0,
        zIndex: 1100
      }}
    >
      <Toolbar 
        disableGutters 
        sx={{ 
          height: 60, 
          width: '100%', 
          px: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.03)',
              opacity: 0.95
            }
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              mr: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))'
            }}
          >
            <img src={tubeAILogo} alt="TubeAI Logo" style={{ width: '100%', height: '100%' }} />
          </Box>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="div"
            sx={{ 
              fontWeight: 700,
              letterSpacing: '0.5px',
              textShadow: '0px 1px 2px rgba(0,0,0,0.15)',
              background: 'linear-gradient(45deg, #ffffff, #e0e0e0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            TubeAI
          </Typography>
        </Box>
        
        {/* PeerTube Instance Selector */}
        <FormControl 
          variant="outlined" 
          size="small"
          sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(5px)',
              transition: 'all 0.3s ease',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderWidth: '1px',
              },
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
              },
              '&:hover fieldset': {
                borderColor: 'white',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'white',
                borderWidth: '1px',
              },
            },
            '& .MuiSelect-icon': {
              color: 'white',
            },
          }}
        >
          <Select
            value={selectedInstance.url}
            onChange={handleInstanceChange}
            displayEmpty

            sx={{
              color: 'white',
              '& .MuiSelect-select': {
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              },
            }}
          >
            {instances.map((instance) => (
              <MenuItem key={instance.url} value={instance.url} sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                py: 1.2,
              }}>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {instance.name}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
